import React, { useCallback, useEffect, useState } from 'react'
import { motion } from 'motion/react'
import {
    forgetDeviceLocation,
    getDeviceLocationStatus,
    setDeviceLocationPref,
} from '../../api/client'

/**
 * The off switch for the only personal coordinates MemGenie persists.
 *
 * Shows when the stored fix was taken, not where — the API deliberately
 * doesn't return the position, since the client never needs it. Turning the
 * toggle off is retroactive server-side: it clears whatever is stored rather
 * than merely stopping new writes.
 */
const LocationSettings: React.FC = () => {
    const [capturedAt, setCapturedAt] = useState<string | null>(null)
    const [optedOut, setOptedOut] = useState(false)
    const [loading, setLoading] = useState(true)
    const [busy, setBusy] = useState(false)

    const refresh = useCallback(async () => {
        try {
            const status = await getDeviceLocationStatus()
            setCapturedAt(status.captured_at ?? null)
            setOptedOut(Boolean(status.opted_out))
        } catch {
            // Leave the section in its last known state rather than showing a
            // scary error for a setting the user probably isn't looking at.
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => { void refresh() }, [refresh])

    const handleToggle = useCallback(async () => {
        const next = !optedOut
        setBusy(true)
        setOptedOut(next)
        try {
            await setDeviceLocationPref(next)
        } catch {
            setOptedOut(!next)
        } finally {
            setBusy(false)
            void refresh()
        }
    }, [optedOut, refresh])

    const handleForget = useCallback(async () => {
        setBusy(true)
        try {
            await forgetDeviceLocation()
            setCapturedAt(null)
        } catch {
            /* leave the timestamp as-is; the user can retry */
        } finally {
            setBusy(false)
        }
    }, [])

    if (loading) return null

    return (
        <div className="mt-8 pt-6 border-t border-gray-200">
            <h2 className="text-lg font-semibold mb-1">Location</h2>
            <p className="text-sm text-gray-600 mb-4">
                Used to find the places you mention near where you actually were.
                We keep only your most recent location, never a history.
            </p>

            <label className="flex items-center gap-3 mb-4 cursor-pointer">
                <input
                    type="checkbox"
                    checked={!optedOut}
                    onChange={handleToggle}
                    disabled={busy}
                    className="h-4 w-4"
                />
                <span className="text-sm">
                    Use my location to tag places
                </span>
            </label>

            <div className="text-sm text-gray-600">
                {capturedAt ? (
                    <div className="flex items-center gap-3">
                        <span>
                            Last updated{' '}
                            {new Date(capturedAt).toLocaleString(undefined, {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                            })}
                        </span>
                        <motion.button
                            type="button"
                            onClick={handleForget}
                            disabled={busy}
                            className="text-sm text-rose-600 hover:text-rose-700 disabled:opacity-50"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        >
                            Forget it
                        </motion.button>
                    </div>
                ) : (
                    <span>No location stored.</span>
                )}
            </div>
        </div>
    )
}

export default LocationSettings
